import React, { useMemo, useState } from 'react';
import { Search, X, GripVertical, ChevronRight } from 'lucide-react';

/**
 * What a step can read, as a list beside its editor: the trace's fields
 * (from GET /eval3/schema?target=, following the scope matrix) and what
 * earlier steps passed on (`extraGroups`, computed from the graph).
 *
 * Click a row to insert its reference at the caret; drag it in. `refOf(field)`
 * decides the text: `t.run.output` for Python, `{{run.output}}` for a prompt.
 *
 * Curated, not a dump: each group shows the fields people reach for and
 * folds the rest behind "more". A keyed row (metadata, attributes) takes a
 * key and inserts `t.run.metadata["tier"]`, so nothing has to be discovered
 * at load time and the whole bag is never dropped into a prompt by
 * accident. `hideTypes` lets a prompt rail leave out lists — every span of
 * a run in a judge prompt is the thing this whole design exists to avoid.
 */
export default function SchemaRail({ schema, refOf, onPick, hint, maxHeight = 260, extraGroups = [], hideTypes = [], hideGroups = [] }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const groups = useMemo(() => {
    const hideT = new Set(hideTypes);
    const hideG = new Set(hideGroups);
    // hideTypes applies to the TRACE only: a judge rail leaves out t.spans,
    // never the excerpt list a gather step passed on — that list is the point.
    const trace = (schema?.groups || []).map((g) => ({ ...g, fields: (g.fields || []).filter((f) => !hideT.has(f.type)) }));
    return [...extraGroups, ...trace]
      .filter((g) => !hideG.has(g.id) && (g.fields || []).length);
  }, [schema, extraGroups, hideTypes, hideGroups]);
  const s = q.trim().toLowerCase();
  const searching = Boolean(s);
  const shown = useMemo(() => {
    if (!searching) return groups;
    return groups
      .map((g) => ({ ...g, fields: g.fields.filter((f) => f.path.toLowerCase().includes(s) || (f.label || '').toLowerCase().includes(s)) }))
      .filter((g) => g.fields.length);
  }, [groups, s, searching]);
  const total = groups.reduce((n, g) => n + g.fields.length, 0);

  if (!groups.length) return <div className="text-[12px] text-[color:var(--text-muted)]">{schema ? 'Nothing to read here.' : 'Loading fields…'}</div>;

  const toggle = (id) => setOpen((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2">
        <Search size={13} className="flex-none text-[color:var(--text-muted)]" aria-hidden />
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${total} fields…`} aria-label="Search trace fields"
          className="h-7 w-full bg-transparent text-[12px] text-[color:var(--text-primary)] outline-none" />
        {q && <button type="button" onClick={() => setQ('')} aria-label="Clear search" className="flex-none text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"><X className="h-3 w-3" /></button>}
      </div>
      <div className="overflow-y-auto rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)]" style={{ maxHeight }}>
        {shown.length === 0 && <div className="px-2.5 py-4 text-center text-[11px] text-[color:var(--text-muted)]">No field matches “{q}”.</div>}
        {shown.map((g) => {
          // Searching shows every hit; otherwise the common rows, and the rest behind "more".
          const common = searching ? g.fields : g.fields.filter((f) => f.common || g.fields.every((x) => !x.common));
          const more = searching ? [] : g.fields.filter((f) => !common.includes(f));
          const expanded = open.has(g.id);
          return (
            <div key={g.id} data-testid={`rail-group-${g.id}`}>
              <div className="sticky top-0 z-[1] border-b border-[color:var(--border-subtle)] bg-[color:var(--surface-sunken)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{g.label}</div>
              {common.map((f) => <Row key={f.path} f={f} refOf={refOf} onPick={onPick} />)}
              {expanded && more.map((f) => <Row key={f.path} f={f} refOf={refOf} onPick={onPick} />)}
              {more.length > 0 && (
                <button type="button" onClick={() => toggle(g.id)} aria-expanded={expanded} aria-label={`${expanded ? 'Fewer' : 'More'} ${g.label} fields`}
                  className="flex w-full items-center gap-1 border-b border-[color:var(--border-subtle)] px-2.5 py-1 text-left text-[11px] text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)] last:border-b-0">
                  <ChevronRight size={11} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} aria-hidden />
                  {expanded ? 'fewer' : `${more.length} more`}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {hint && <p className="mt-1.5 text-[11px] leading-snug text-[color:var(--text-muted)]">{hint}</p>}
    </div>
  );
}

/**
 * One field on ONE line: the name people know it by, the type at the edge, a
 * grip on hover. The path is what gets inserted, not what has to be read —
 * it lives on the hover title with the full label, because a second line per
 * row doubles the height of the rail to repeat what the name already said.
 */
function Row({ f, refOf, onPick }) {
  const [key, setKey] = useState('');
  const withKey = (field) => {
    if (!field.keyed || !key.trim()) return field;
    const k = key.trim();
    return { ...field, path: `${field.path}.${k}`, python: `${field.python}[${JSON.stringify(k)}]`, prompt: `{{${field.path}.${k}}}` };
  };
  const label = f.label && f.label !== f.path ? f.label.replace(/\s*\(.*$/, '') : f.path;
  const hover = [f.path + (f.keyed ? '.…' : ''), f.label && f.label !== label ? f.label : null].filter(Boolean).join(' — ');
  return (
    <div role="button" tabIndex={0} draggable
      onDragStart={(e) => { e.dataTransfer.setData('text/plain', refOf(withKey(f))); e.dataTransfer.effectAllowed = 'copy'; }}
      onClick={() => onPick(withKey(f))}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(withKey(f)); } }}
      title={hover}
      className="group flex w-full cursor-grab items-center gap-2 border-b border-[color:var(--border-subtle)] px-2.5 py-1.5 text-left transition-colors last:border-b-0 hover:bg-surface-hover active:cursor-grabbing">
      <GripVertical className="h-3 w-3 shrink-0 text-transparent group-hover:text-[color:var(--text-disabled)]" aria-hidden />
      <div className="min-w-0 flex-1 truncate text-[12px] text-[color:var(--text-primary)]">{label}</div>
      {f.keyed && (
        <input value={key} onChange={(e) => setKey(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') onPick(withKey(f)); }}
          placeholder="key" aria-label={`${f.path} key`}
          className="h-6 w-20 shrink-0 rounded border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-1.5 font-mono text-[10.5px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]" />
      )}
      <span className="shrink-0 rounded bg-[color:var(--surface-sunken)] px-1 py-px text-[9.5px] text-[color:var(--text-muted)]">{f.type}</span>
    </div>
  );
}
